import { ChangeDetectionStrategy, Component, computed, inject, input } from "@angular/core";
import { IconModule, PhosphorIconName } from "@storeblocks/icon-ng";
import { DevRowDirective } from "./dev-row.directive";
import { defaultIconForType } from "./dev-row-type";

/**
 * One dev panel control: icon, label, optional description, and the control
 * itself projected on the right.
 *
 * Composes DevRowDirective via hostDirectives so filtering, route tagging and
 * availability all keep working through the directive's existing inputs —
 * there is no parallel registry to keep in sync.
 */
@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: "dev-row",
  standalone: true,
  imports: [IconModule],
  hostDirectives: [
    {
      directive: DevRowDirective,
      inputs: ["devRow", "devRowKeywords", "devRowAvailability", "devRowRoutes", "devRowType"],
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="flex items-center justify-between gap-08 py-08">
      <div class="flex min-w-0 items-center gap-08">
        <stb-icon data-role="type-icon" [name]="resolvedIcon()" class="shrink-0" />
        <div class="min-w-0">
          <div class="font-body truncate">{{ row.label() }}</div>
          @if (description(); as text) {
            <div class="font-details text-[var(--p-color-text-secondary)]">{{ text }}</div>
          }
          @if (reason(); as text) {
            <div class="font-details flex items-center gap-04">
              <stb-icon data-role="reason-icon" [name]="reasonIcon()" />
              <span>{{ text }}</span>
            </div>
          }
        </div>
      </div>
      <div class="flex w-[250px] shrink-0 justify-end">
        <ng-content />
      </div>
    </div>
    <ng-content select="[devRowBody]" />
  `,
})
export class DevRowComponent {
  readonly icon = input<PhosphorIconName | undefined>(undefined);
  readonly description = input<string | undefined>(undefined);

  protected readonly row = inject(DevRowDirective, { self: true });
  protected readonly reason = computed(() => {
    const availability = this.row.availability();
    return availability.tier === "available" ? undefined : availability.reason;
  });
  protected readonly reasonIcon = computed<PhosphorIconName>(() =>
    this.row.availability().tier === "blocked" ? "lock" : "warning",
  );
  protected readonly resolvedIcon = computed<PhosphorIconName>(
    () => this.icon() ?? defaultIconForType(this.row.type()),
  );
}
